import { Link } from "react-router-dom";
import { products } from "../../../content";
import type { Product } from "../../../types/content";
import { productBrand } from "../../../utils/productName";

/**
 * ============================================================================
 * Products — the Home teaser.
 * ============================================================================
 *
 * Four flagship products and a link to /products.
 *
 * WHICH four is an editorial choice, so it lives HERE and not in
 * products.json — the D-065 ruling applied at the point it was meant for. The
 * content layer records what exists; a component decides what leads.
 *
 * THE FACTS ARE STILL RESOLVED FROM THE LAYER, BY ID. Nothing below is a
 * literal: the ids name records and the copy comes out of them, so a corrected
 * product name or involvement reaches this strip without anyone remembering it
 * exists. A missing id is loud in development and silently skipped in
 * production, because a teaser quietly rendering three cards instead of four is
 * the D-065 failure in miniature.
 *
 * ----------------------------------------------------------------------------
 * THE CARDS EXPAND — 2026-08-23 (D-075)
 * ----------------------------------------------------------------------------
 * Collapsed, a card says what it did before: the brand and the involvement.
 * Open, it adds the SAME `description` and `outcomes` the `/products` card
 * shows — read from the same record, through the same loader, rendered through
 * the same `ProductOutcomes` component. No string here is a copy of anything on
 * that page, and there is nothing to keep in sync.
 *
 * NATIVE `<details>`, NOT A HAND-ROLLED BUTTON, and the brief named the other
 * one. `<summary>` is already exposed to assistive technology as a disclosure
 * control carrying an expanded/collapsed state — the `aria-expanded` the brief
 * asked for, supplied by the element rather than by an attribute that can drift
 * out of step with the state it describes. It also brings Enter, Space, the
 * focus ring, and in-page find (Chrome opens a closed `<details>` to reveal a
 * match) without any of it being reimplemented, and it works with JavaScript
 * disabled. `/stories` has shipped 23 of them since D-020 on the same
 * reasoning; this is the site's disclosure pattern, not a new one.
 *
 * STATE IS HELD IN REACT ONLY so the label and the arrow can follow it. `open`
 * starts FALSE on the server and on the first client render, so the markup
 * React hydrates is the markup the pre-render wrote — a state that differed
 * between the two would cost the whole page a client re-render (D-042).
 *
 * THE PANEL IS IN THE PRE-RENDERED HTML EITHER WAY. A closed `<details>` still
 * contains its children; the browser does not paint them. That is the whole
 * reason to prefer it over `{open && <panel/>}`, which would ship four empty
 * cards to a crawler while showing a reader four full ones.
 *
 * THE ACCESSIBLE NAME CARRIES THE PRODUCT AND THE VISIBLE LABEL DOES NOT.
 * Four controls all named "Read the detail" are indistinguishable in a screen
 * reader's list of controls, so `aria-label` appends the brand. It is built
 * from the same string the eye reads, so WCAG 2.5.3 (Label in Name) holds by
 * construction — the visible text is a prefix of the accessible name, and both
 * are derived from one `open` flag and cannot drift.
 *
 * NOT a visually-hidden span: `footer.scss` declares a `.sr-only` of its own
 * that loads AFTER Tailwind's and wins, and it is the weak version — `width:
 * .5px; height: 1px; position: absolute; overflow: hidden`, with no `clip` and
 * no containing block here to position against. Tailwind's `sr-only` utility is
 * effectively dead on this site. Recorded in TODO; not fixed here.
 *
 * NOTHING IS PINNED TO THE FOOT OF THE CARD ANY MORE, and that is a deliberate
 * 20px change. The involvement line carried `mt-auto`, which pinned it to the
 * bottom of whatever height the card happened to have. Measured cost in the
 * closed state: exactly one card moves, SFT Evolve at 1280, whose one-line
 * involvement goes from y=65 to y=45 in a 126px card. Every other card at every
 * other width is unchanged, because their content already filled the box.
 *
 * ============================================================================
 * AND THE ROW NO LONGER STRETCHES — D-080, and this is the defect `mt-auto`
 * was only a symptom of.
 * ============================================================================
 * D-075 wrote here that "the moment one card opens, its three neighbours grow
 * with it" and treated that as the cost of a grid. It is not a cost, it is the
 * bug, and it was reported as one: opening a card appears to open all four.
 * Measured at 1280 — four cards at 185px, then FOUR at 530px after opening the
 * first, with `details.open` false on three of them. At 768 (two rows) only the
 * row-mate inflates; at 390 (four rows) none do, which is why it reads as a
 * desktop-only fault.
 *
 * THE STATE WAS NEVER SHARED. Each card is its own component instance with its
 * own `open` flag, and `<details>` without a `name` attribute is independent by
 * construction. The `open` flags after clicking the first card are
 * [true, false, false, false] at every width. What was wrong was purely the
 * geometry, and `items-start` on the track is the whole fix.
 */

/**
 * Editorial pick, by content-layer id, IN RENDER ORDER. Owner brief 2026-08-20,
 * reordered 2026-08-22 (D-072).
 *
 * SFT Evolve leads by owner ruling. The count is held at four — the grid is
 * `lg:grid-cols-4` and a fifth card would silently start a second row — so
 * adding one at the top DISPLACED one at the bottom: `sft-warehouse-suite`
 * left the teaser. It is not gone from the site; `/products` renders all nine,
 * and dropping the last entry rather than a middle one is what keeps the
 * remaining three in the order the 2026-08-20 brief set.
 */
const FEATURED: readonly string[] = [
	"sft-evolve-hrms",
	"enterprise-compliance-platform",
	"sft-insurance-hub",
	"sft-smartspace"
];

const featured = (): Product[] =>
	FEATURED.map((id: string): Product | null => {
		const product = products.find((p: Product) => p.id === id) ?? null;
		if (!product && !import.meta.env.PROD) {
			// eslint-disable-next-line no-console
			console.error(`ProductsTeaser: no product "${id}" in products.json.`);
		}
		return product;
	}).filter((p: Product | null): p is Product => p !== null);

const TeaserCard = ({ product }: { product: Product }): JSX.Element => {
	const brand = productBrand(product.name);

	/* THE WHOLE CARD IS ONE LINK, AND THE DISCLOSURE HAD TO GO FOR IT (D-099).
	   Owner ruling: the card redirects. It cannot do that and keep the
	   `<details>` it carried since D-075, because an `<a>` may not contain
	   interactive content — a `<summary>` inside a link is invalid HTML, a
	   parser rewrites it, and React only hydrates markup it produced itself, so
	   the whole page would fall back to a client re-render (D-042 found exactly
	   one such element and it disabled hydration site-wide).

	   The pattern is the one the Workshops teaser already uses: a real link on
	   the heading, stretched over the card with `after:inset-0`. That is ONE tab
	   stop and one accessible name, where a card wrapped in an anchor plus a
	   heading link would be two links to the same place.

	   WHAT THE CARD LOST AND WHAT IT GAINED. It no longer expands the full
	   description and the outcome rail in place; it now shows the description
	   and the tag rail without being asked, which is strictly more than the
	   brand-and-involvement it showed while closed. The outcomes live on
	   `/products`, where the anchor lands. */
	return (
		<article className="card-shell group relative">
			<div className="card-face flex h-full flex-col p-6">
				{/* Brand ramp, not a category hue — see the note in Services: on this
				    site a `--cat-*` hue is information, and a decorative accent takes
				    the brand ramp instead so it does not imply a taxonomy. */}
				<span
					aria-hidden="true"
					className="block h-[3px] w-10 rounded-full bg-gradient-to-r from-[var(--grad-brand-from)] via-[var(--grad-brand-via)] to-[var(--grad-brand-to)] transition-[width] duration-300 group-hover:w-16"
				/>

				<h3 className="mt-5 text-base font-bold leading-snug text-[var(--text-strong)]">
					<Link
						to={`/products#${product.id}`}
						className="rounded-[4px] transition-colors duration-200 after:absolute after:inset-0 after:rounded-[1rem] after:content-[''] hover:text-[var(--accent-link-hover)] focus:outline-none focus-visible:ring-4 focus-visible:ring-[var(--ring-accent)]"
					>
						{brand}
					</Link>
				</h3>

				<p className="mt-1 text-sm text-[var(--text-muted)]">{product.involvement}</p>

				{/* Clamped, not truncated in the layer: the full sentence stays in
				    `products.json` and renders whole on `/products`. Three lines is
				    what keeps four cards on one row the same height without the
				    shortest one carrying a block of dead space. */}
				<p className="mt-3 line-clamp-3 text-sm leading-relaxed text-[var(--text-secondary)]">
					{product.description}
				</p>

				<div className="mt-auto pt-5">
					<div className="flex flex-wrap gap-2">
						{product.tags.map((tag: string) => (
							<span key={tag} className="chip-quiet">
								{tag}
							</span>
						))}
					</div>

					{/* aria-hidden: the heading link above already names the
					    destination, so announcing this would give a screen-reader
					    user two links to one page. Same call the Workshops teaser
					    makes for its "Read more". */}
					<p
						aria-hidden="true"
						className="mt-4 text-sm font-medium text-[var(--accent-link)] transition-colors duration-200 group-hover:text-[var(--accent-link-hover)]"
					>
						Open the detail &rarr;
					</p>
				</div>
			</div>
		</article>
	);
};

export default function ProductsTeaser(): JSX.Element {
	const picks = featured();

	return (
		<section className="section-shell" id="products" aria-labelledby="products-teaser-heading">
			<div className="section-rail">
				<p className="section-eyebrow">Owned Product Suite</p>

				<h2 id="products-teaser-heading" className="heading">
					<span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--grad-brand-from)] via-[var(--grad-brand-via)] to-[var(--grad-brand-to)]">
						Products
					</span>
				</h2>

				<p className="section-standfirst">
					The multi-tenant enterprise SaaS suite built and governed in-house at Shyam Future Tech.
				</p>

				{/* `items-start` IS GONE, AND ITS REASON WENT WITH THE DISCLOSURE
				    (D-099). D-080 added it because a card could GROW: opening one
				    `<details>` stretched its row-mates to match while they stayed
				    closed — 185px to 530px at 1280, with `details.open` false on
				    three of them — which reads as "expanding one expanded them all"
				    even though the state was never shared. No card expands any
				    more, so start-alignment now buys nothing and costs the thing
				    the owner asked for: four cards of visibly different heights.
				    Stretching is what makes them uniform, and `auto-rows-fr`
				    equalises ACROSS rows too, which matters at `sm` where four
				    cards sit in two rows.

				    A rule kept past the end of its reason is how D-098's dead
				    `.node img` came to beat the shared logo plate, so this one is
				    removed rather than left in place with a stale comment. */}
				<div className="mt-10 grid auto-rows-fr grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
					{picks.map((p: Product) => (
						<TeaserCard key={p.id} product={p} />
					))}
				</div>

				<div className="mt-10">
					<Link
						to="/products"
						className="inline-block min-h-[24px] text-sm font-medium text-[var(--accent-link)] transition-colors duration-200 hover:text-[var(--accent-link-hover)]"
					>
						See the full product suite →
					</Link>
				</div>
			</div>
		</section>
	);
}
